import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import {
    DndContext,
    PointerSensor,
    KeyboardSensor,
    useSensor,
    useSensors,
    closestCenter,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
    Plus,
    FloppyDisk,
    ArrowLeft,
    Barbell,
} from '@phosphor-icons/react';
import { api, formatApiErrorDetail } from '@/lib/api';
import { ExercisePicker } from '@/components/ExercisePicker';
import { SortableExerciseRow } from '@/components/SortableExerciseRow';
import { WORKOUT_BUILDER } from '@/constants/testIds';

function newEntryId(exerciseId) {
    return `${exerciseId}-${Math.random().toString(36).slice(2, 9)}`;
}

export default function WorkoutBuilder() {
    const navigate = useNavigate();
    const { id: templateId } = useParams();
    const isEdit = Boolean(templateId);

    const [name, setName] = useState('');
    const [notes, setNotes] = useState('');
    // entries: [{ _rowId, exercise_id, target_sets, target_reps, notes }]
    const [entries, setEntries] = useState([]);
    // exerciseLookup: { [exercise_id]: Exercise }
    const [exerciseLookup, setExerciseLookup] = useState({});
    const [pickerOpen, setPickerOpen] = useState(false);
    const [loading, setLoading] = useState(isEdit);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    useEffect(() => {
        if (!isEdit) return;
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const { data } = await api.get(`/templates/${templateId}`);
                if (cancelled) return;
                setName(data.name);
                setNotes(data.notes || '');
                setEntries(
                    (data.exercises || []).map((e) => ({
                        _rowId: newEntryId(e.exercise_id),
                        exercise_id: e.exercise_id,
                        target_sets: e.target_sets,
                        target_reps: e.target_reps,
                        notes: e.notes || '',
                    }))
                );
                // Hydrate lookup
                const ids = (data.exercises || []).map((e) => e.exercise_id);
                if (ids.length) {
                    const { data: catalog } = await api.get('/exercises');
                    if (!cancelled) {
                        const map = {};
                        catalog.forEach((c) => {
                            map[c.id] = c;
                        });
                        setExerciseLookup(map);
                    }
                }
            } catch (e) {
                if (!cancelled) {
                    setError(
                        formatApiErrorDetail(e.response?.data?.detail) ||
                            e.message
                    );
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [templateId, isEdit]);

    function addExercises(picks) {
        // Merge picked exercises into lookup + entries
        setExerciseLookup((prev) => {
            const next = { ...prev };
            picks.forEach((p) => {
                next[p.id] = p;
            });
            return next;
        });
        setEntries((prev) => [
            ...prev,
            ...picks.map((p) => ({
                _rowId: newEntryId(p.id),
                exercise_id: p.id,
                target_sets: 3,
                target_reps: 10,
                notes: '',
            })),
        ]);
    }

    function updateEntry(rowId, patch) {
        setEntries((prev) =>
            prev.map((e) => (e._rowId === rowId ? { ...e, ...patch } : e))
        );
    }

    function removeEntry(rowId) {
        setEntries((prev) => prev.filter((e) => e._rowId !== rowId));
    }

    function onDragEnd(event) {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        setEntries((prev) => {
            const oldIndex = prev.findIndex((e) => e._rowId === active.id);
            const newIndex = prev.findIndex((e) => e._rowId === over.id);
            if (oldIndex === -1 || newIndex === -1) return prev;
            return arrayMove(prev, oldIndex, newIndex);
        });
    }

    const canSave = useMemo(
        () =>
            name.trim().length > 0 &&
            entries.length > 0 &&
            entries.every(
                (e) =>
                    Number.isFinite(Number(e.target_sets)) &&
                    Number(e.target_sets) >= 1 &&
                    Number.isFinite(Number(e.target_reps)) &&
                    Number(e.target_reps) >= 1
            ),
        [name, entries]
    );

    async function save() {
        if (!canSave || saving) return;
        setSaving(true);
        setError('');
        const payload = {
            name: name.trim(),
            notes: notes.trim() || null,
            exercises: entries.map((e) => ({
                exercise_id: e.exercise_id,
                target_sets: Number(e.target_sets),
                target_reps: Number(e.target_reps),
                notes: e.notes?.trim() || null,
            })),
        };
        try {
            if (isEdit) {
                await api.patch(`/templates/${templateId}`, payload);
            } else {
                await api.post('/templates', payload);
            }
            navigate('/workouts', { replace: true });
        } catch (e) {
            setError(
                formatApiErrorDetail(e.response?.data?.detail) || e.message
            );
        } finally {
            setSaving(false);
        }
    }

    if (loading) {
        return (
            <main className="mx-auto max-w-3xl px-5 sm:px-8 pt-16 pb-32 text-center">
                <p className="uppercase-mono text-muted-foreground animate-soft-pulse">
                    Loading template…
                </p>
            </main>
        );
    }

    return (
        <main
            data-testid={WORKOUT_BUILDER.root}
            className="mx-auto max-w-3xl px-5 sm:px-8 pt-10 sm:pt-14 pb-32"
        >
            <div className="animate-fade-up">
                <Link
                    to="/workouts"
                    className="inline-flex items-center gap-1.5 uppercase-mono text-muted-foreground hover:text-foreground transition-colors"
                >
                    <ArrowLeft size={14} weight="bold" />
                    Back to workouts
                </Link>
                <h1 className="mt-3 font-display font-extrabold tracking-tighter text-4xl sm:text-5xl">
                    {isEdit ? 'Edit template' : 'New template'}
                </h1>
                <p className="mt-3 text-muted-foreground max-w-xl">
                    Build a re-usable workout you can start with one tap. Set
                    targets for each exercise; weight and RPE are picked live
                    during the session.
                </p>
            </div>

            {/* Meta */}
            <section className="mt-8 glass-card rounded-3xl p-6 sm:p-7 space-y-4">
                <label className="block">
                    <span className="uppercase-mono text-muted-foreground block mb-2">
                        Name
                    </span>
                    <input
                        data-testid={WORKOUT_BUILDER.nameInput}
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Push Day · Heavy"
                        className="w-full h-12 rounded-2xl border border-foreground/15 bg-foreground/5 px-4 outline-none focus:border-foreground/40 transition-colors text-lg font-semibold tracking-tight placeholder:text-muted-foreground/60 placeholder:font-normal"
                    />
                </label>
                <label className="block">
                    <span className="uppercase-mono text-muted-foreground block mb-2">
                        Notes (optional)
                    </span>
                    <textarea
                        data-testid={WORKOUT_BUILDER.notesInput}
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        rows={2}
                        placeholder="Block focus, prescription, reminders…"
                        className="w-full rounded-2xl border border-foreground/15 bg-foreground/5 px-4 py-3 outline-none focus:border-foreground/40 transition-colors placeholder:text-muted-foreground/60 resize-none"
                    />
                </label>
            </section>

            {/* Exercises */}
            <section className="mt-8">
                <div className="flex items-end justify-between gap-3 mb-4">
                    <div>
                        <span className="uppercase-mono text-muted-foreground">
                            {entries.length} exercise
                            {entries.length === 1 ? '' : 's'}
                        </span>
                        <h2 className="font-display font-bold tracking-tight text-2xl mt-1">
                            Plan
                        </h2>
                    </div>
                    <button
                        type="button"
                        onClick={() => setPickerOpen(true)}
                        data-testid={WORKOUT_BUILDER.addExerciseBtn}
                        className="inline-flex items-center gap-2 h-11 px-4 rounded-2xl border border-foreground/15 bg-foreground/5 hover:bg-foreground/10 active:scale-[0.98] transition-all font-semibold"
                    >
                        <Plus size={16} weight="bold" />
                        Add
                    </button>
                </div>

                {entries.length === 0 ? (
                    <div
                        data-testid={WORKOUT_BUILDER.emptyState}
                        className="glass-card rounded-3xl p-10 text-center"
                    >
                        <span className="grid place-items-center w-12 h-12 rounded-2xl bg-foreground/5 border border-foreground/10 mx-auto mb-4">
                            <Barbell size={20} weight="bold" />
                        </span>
                        <p className="font-display font-bold text-lg tracking-tight">
                            Empty template
                        </p>
                        <p className="text-muted-foreground text-sm mt-1 max-w-xs mx-auto">
                            Add at least one exercise to save. Drag to reorder
                            them once added.
                        </p>
                        <button
                            type="button"
                            onClick={() => setPickerOpen(true)}
                            className="mt-5 inline-flex items-center gap-2 h-11 px-5 rounded-2xl bg-primary text-primary-foreground font-bold tracking-tight shadow-neon hover:-translate-y-[2px] active:scale-[0.98] transition-all"
                        >
                            <Plus size={16} weight="bold" /> Add exercises
                        </button>
                    </div>
                ) : (
                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={onDragEnd}
                    >
                        <SortableContext
                            items={entries.map((e) => e._rowId)}
                            strategy={verticalListSortingStrategy}
                        >
                            <ul className="space-y-3">
                                {entries.map((entry, idx) => (
                                    <SortableExerciseRow
                                        key={entry._rowId}
                                        id={entry._rowId}
                                        index={idx}
                                        exercise={
                                            exerciseLookup[entry.exercise_id]
                                        }
                                        entry={entry}
                                        onChange={(next) =>
                                            updateEntry(entry._rowId, next)
                                        }
                                        onRemove={() =>
                                            removeEntry(entry._rowId)
                                        }
                                    />
                                ))}
                            </ul>
                        </SortableContext>
                    </DndContext>
                )}
            </section>

            {error && (
                <p className="mt-6 text-sm text-[#FF4D4D] font-medium">
                    {error}
                </p>
            )}

            {/* Sticky footer actions */}
            <div className="mt-10 sticky bottom-4 z-30">
                <div className="glass-card rounded-2xl p-3 flex items-center justify-between gap-3">
                    <button
                        type="button"
                        onClick={() => navigate('/workouts')}
                        data-testid={WORKOUT_BUILDER.cancelBtn}
                        className="h-12 px-5 rounded-xl border border-foreground/15 bg-foreground/5 hover:bg-foreground/10 active:scale-[0.98] transition-all font-semibold"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={save}
                        disabled={!canSave || saving}
                        data-testid={WORKOUT_BUILDER.saveBtn}
                        className="flex-1 h-12 rounded-xl bg-primary text-primary-foreground font-bold tracking-tight shadow-neon hover:-translate-y-[2px] active:scale-[0.99] transition-all disabled:opacity-50 disabled:hover:translate-y-0 inline-flex items-center justify-center gap-2"
                    >
                        <FloppyDisk size={18} weight="bold" />
                        {saving
                            ? 'Saving…'
                            : isEdit
                              ? 'Save changes'
                              : 'Save template'}
                    </button>
                </div>
            </div>

            <ExercisePicker
                open={pickerOpen}
                onClose={() => setPickerOpen(false)}
                onAdd={addExercises}
            />
        </main>
    );
}
